import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/store/auth.store';
import { usePermisosTareas } from '@/hooks/useTareas';

const AGRUPAR_MS = 150;

// Configuración que no cambia con la actividad de las tareas: recargarla solo estorbaría a lo importante.
const CONFIG_ESTATICA = new Set(['tableros', 'etiquetas', 'asignables', 'plantilla']);

interface CambioTarea {
  tareaId: string;
  tableroId: string;
  tipo: string;
  /** Usuario que hizo el cambio. */
  origen?: string;
}

/**
 * Mantiene al día las vistas de tareas cuando otro usuario las modifica. El servidor solo avisa que algo
 * cambió (sin datos); aquí se vuelve a pedir lo que está en pantalla, así la regla de visibilidad la sigue
 * aplicando el backend. Los cambios propios se ignoran (ya se reflejaron al guardar) y las ráfagas se
 * agrupan en una sola recarga.
 */
export function useTareasRealtime() {
  const qc = useQueryClient();
  const usuarioId = useAuthStore(s => s.usuario?.id);
  const { puedeUsar } = usePermisosTareas();

  useEffect(() => {
    if (!usuarioId || !puedeUsar) return;
    const socket = getSocket();
    let temporizador: ReturnType<typeof setTimeout> | undefined;

    const alCambiar = (cambio: CambioTarea) => {
      if (cambio.origen === usuarioId) return;
      clearTimeout(temporizador);
      // Solo se recargan las consultas activas (las que hay en pantalla).
      temporizador = setTimeout(() => qc.invalidateQueries({
        queryKey: ['tareas'],
        predicate: q => !CONFIG_ESTATICA.has(String(q.queryKey[1])),
      }), AGRUPAR_MS);
    };

    socket.on('tareas:cambio', alCambiar);
    return () => {
      socket.off('tareas:cambio', alCambiar);
      clearTimeout(temporizador);
    };
  }, [usuarioId, puedeUsar, qc]);
}
